import { GraphQLResult } from '@aws-amplify/api-graphql';
import { Auth } from 'aws-amplify';
import { reportCdkRefusal } from 'app/services/session/cdkRefusalSignal';
import { captureException } from '@sentry/react';

/**
 * Client for the CDK `v2-webauthn` AppSync resolvers (SEE-213 passkeys).
 * Mirrors UserService's transport: POST to the CDK AppSync endpoint with the
 * Cognito ID token. Covers passkey REGISTRATION + MANAGEMENT only — passkey
 * LOGIN is a Cognito CUSTOM_AUTH flow handled separately (see passkeyClient).
 */

export interface Passkey {
  credentialId: string;
  name: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  deviceType: string | null;
}

export interface PasskeyRegistrationOptions {
  /** JSON-stringified PublicKeyCredentialCreationOptionsJSON. */
  optionsJson: string;
}

export interface PasskeyRegistrationResult {
  verified: boolean;
  passkey: Passkey | null;
}

const startPasskeyRegistrationMutation = /* GraphQL */ `
  mutation StartPasskeyRegistration {
    startPasskeyRegistration {
      optionsJson
    }
  }
`;

const verifyPasskeyRegistrationMutation = /* GraphQL */ `
  mutation VerifyPasskeyRegistration($responseJson: String!, $name: String) {
    verifyPasskeyRegistration(responseJson: $responseJson, name: $name) {
      verified
      passkey {
        credentialId
        name
        createdAt
        lastUsedAt
        deviceType
      }
    }
  }
`;

const listPasskeysQuery = /* GraphQL */ `
  query ListPasskeys {
    listPasskeys {
      credentialId
      name
      createdAt
      lastUsedAt
      deviceType
    }
  }
`;

const deletePasskeyMutation = /* GraphQL */ `
  mutation DeletePasskey($credentialId: String!) {
    deletePasskey(credentialId: $credentialId) {
      success
    }
  }
`;

const renamePasskeyMutation = /* GraphQL */ `
  mutation RenamePasskey($credentialId: String!, $name: String!) {
    renamePasskey(credentialId: $credentialId, name: $name) {
      credentialId
      name
      createdAt
      lastUsedAt
      deviceType
    }
  }
`;

class WebAuthnService {
  private readonly endpoint: string;

  constructor() {
    this.endpoint = process.env.REACT_APP_CDK_APPSYNC_ENDPOINT || '';
  }

  async startPasskeyRegistration(): Promise<PasskeyRegistrationOptions> {
    const result = await this.execute<{ startPasskeyRegistration: PasskeyRegistrationOptions | null }>(
      startPasskeyRegistrationMutation,
      {}
    );
    return this.required(result.startPasskeyRegistration, 'startPasskeyRegistration');
  }

  async verifyPasskeyRegistration(responseJson: string, name?: string): Promise<PasskeyRegistrationResult> {
    const result = await this.execute<{ verifyPasskeyRegistration: PasskeyRegistrationResult | null }>(
      verifyPasskeyRegistrationMutation,
      { responseJson, name: name ?? null }
    );
    return this.required(result.verifyPasskeyRegistration, 'verifyPasskeyRegistration');
  }

  async listPasskeys(): Promise<Passkey[]> {
    const result = await this.execute<{ listPasskeys: Passkey[] }>(listPasskeysQuery, {});
    return result.listPasskeys ?? [];
  }

  async deletePasskey(credentialId: string): Promise<boolean> {
    const result = await this.execute<{ deletePasskey: { success: boolean } | null }>(deletePasskeyMutation, {
      credentialId,
    });
    return this.required(result.deletePasskey, 'deletePasskey').success;
  }

  async renamePasskey(credentialId: string, name: string): Promise<Passkey> {
    const result = await this.execute<{ renamePasskey: Passkey | null }>(renamePasskeyMutation, {
      credentialId,
      name,
    });
    return this.required(result.renamePasskey, 'renamePasskey');
  }

  /** The schema allows null results without errors; fail with a real message
   * instead of an opaque null property access downstream. */
  private required<T>(value: T | null | undefined, operation: string): T {
    if (value == null) {
      throw new Error(`Passkey request failed (${operation} returned no data). Please try again.`);
    }
    return value;
  }

  private async execute<T>(query: string, variables: Record<string, unknown>): Promise<T> {
    if (!this.endpoint) {
      throw new Error('Passkeys are currently unavailable. Please contact support.');
    }
    try {
      const session = await Auth.currentSession();
      const jwt = session.getIdToken().getJwtToken();
      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: { 'content-type': 'application/json', Authorization: jwt },
        body: JSON.stringify({ query, variables }),
      });
      const json = (await response.json()) as GraphQLResult<T> & { errors?: Array<{ message: string }> };
      reportCdkRefusal(json?.errors, { authMode: 'cognito' });
      if (!response.ok || json.errors?.length) {
        const message = json?.errors?.map((e) => e.message).join(', ') || `HTTP ${response.status}`;
        throw new Error(message);
      }
      return json.data as T;
    } catch (e) {
      captureException(e, { tags: { area: 'webauthn' } });
      throw e;
    }
  }
}

export const webAuthnService = new WebAuthnService();
